import React from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import { Box, Fade, withStyles } from '@material-ui/core';
import { Loader, Theme } from '@orijinworks/frontend-commons';
import { withRouter } from '../../../../../util/router-compatibility';
import withRolePermission from '../../../Feeds/withRolePermission';
import { REENTRY_PLANS_LIST_PATH } from '../../constants/reentry-planning.constants';
import { useCasePlanCreateHook } from './useCasePlanCreateHook';
import CasePlanCreateHeader from './components/CasePlanCreateHeader';
import PlanTypeSection from './components/PlanTypeSection';
import ResidentSection from './components/ResidentSection';
import TemplateNameSection from './components/TemplateNameSection';
import PlanStructureSection from './components/PlanStructureSection';
import EditorsSection from './components/EditorsSection';
import CasePlanCreateActions from './components/CasePlanCreateActions';
import styles from './CasePlanCreate.styles';

/**
 * Create a New Plan / Template page — thin layout composing section sub-components.
 * Business logic lives in useCasePlanCreateHook; data comes from the create service.
 * Mount is gated by withRolePermission (/user/token) then case_plan_reentry_create.
 */
function CasePlanCreate({ classes }) {
  const {
    canAccessCreate,
    isLoading,
    isGlobalLoading,
    isSubmitting,
    canCreate,
    isTemplateMode,
    isSettingsVisible,
    planTypeFadeMs,
    pageTitle,
    structureSectionTitle,
    createLabel,
    selectedPlanType,
    selectedResidents,
    templateName,
    planStructure,
    templates,
    selectedTemplate,
    editors,
    handleBack,
    handleCancel,
    handleSwitchPlanType,
    handleResidentsChange,
    handleTemplateNameChange,
    handlePlanStructureChange,
    handleTemplateChange,
    handleEditorsChange,
    handleCreatePlan,
  } = useCasePlanCreateHook();

  if (!canAccessCreate) {
    return <Navigate to={REENTRY_PLANS_LIST_PATH} replace />;
  }

  if (isLoading) {
    return (
      <Theme>
        <Box className={classes.page}>
          <Loader isLoading />
        </Box>
      </Theme>
    );
  }

  return (
    <Theme>
      <Loader isLoading={isGlobalLoading} />
      <Box
        className={classes.page}
        role="main"
        aria-label={pageTitle}
      >
        <Box className={classes.card}>
          <Fade in={isSettingsVisible} timeout={planTypeFadeMs}>
            <Box>
              <CasePlanCreateHeader title={pageTitle} onBack={handleBack} />
              <Box className={classes.settings}>
                <PlanTypeSection
                  planType={selectedPlanType}
                  onSwitchPlanType={handleSwitchPlanType}
                />
                {isTemplateMode ? (
                  <TemplateNameSection
                    templateName={templateName}
                    onTemplateNameChange={handleTemplateNameChange}
                  />
                ) : (
                  <ResidentSection
                    selectedResidents={selectedResidents}
                    onResidentsChange={handleResidentsChange}
                  />
                )}
                <PlanStructureSection
                  title={structureSectionTitle}
                  planStructure={planStructure}
                  templates={templates}
                  selectedTemplate={selectedTemplate}
                  onPlanStructureChange={handlePlanStructureChange}
                  onTemplateChange={handleTemplateChange}
                />
                <EditorsSection
                  editors={editors}
                  onEditorsChange={handleEditorsChange}
                />
              </Box>
              <CasePlanCreateActions
                canCreate={canCreate}
                isSubmitting={isSubmitting}
                createLabel={createLabel}
                onCreate={handleCreatePlan}
                onCancel={handleCancel}
              />
            </Box>
          </Fade>
        </Box>
      </Box>
    </Theme>
  );
}

CasePlanCreate.propTypes = {
  classes: PropTypes.object.isRequired,
};

const StyledCasePlanCreate = withRouter(withStyles(styles)(CasePlanCreate));

export default withRolePermission(StyledCasePlanCreate);
